import React, { useState, useEffect } from 'react';
import { ChevronDown, Eye, Edit, X, Plus, Paperclip, AlertTriangle, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function DocenteDocumentacion() {
  const [documentos, setDocumentos] = useState([]);
  const [requisitos, setRequisitos] = useState([]);
  const [carreras, setCarreras] = useState([]);
  const [menciones, setMenciones] = useState([]);
  const [asignaturas, setAsignaturas] = useState([]);
  const [paralelos, setParalelos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cargadoHabilitado, setCargadoHabilitado] = useState(true);
  const [periodoInfo, setPeriodoInfo] = useState(null);

  // Años desde 2020 hasta año actual + 1
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2020 + 2 }, (_, i) => 2020 + i);

  const [filters, setFilters] = useState({
    ano: currentYear.toString()
  });

  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [formData, setFormData] = useState({
    tipo_documento: '',
    id_requisito: '',
    id_carrera: '',
    id_mencion: '',
    id_asignatura: '',
    paralelo: '',
    archivo: null
  });

  useEffect(() => {
    verificarPeriodoDocumentacion();
    loadDocumentos();
    loadRequisitos();
    loadCarreras();
    loadAsignaturas();
  }, [filters.ano]);

  const getToken = () => localStorage.getItem('token');

  const verificarPeriodoDocumentacion = async () => {
    try {
      // Search with exact names that exist in database
      const response = await fetch(`${API_URL}/direccion/parametrizaciones/verificar/${encodeURIComponent('Cargado de documentación académica (docentes)')}`);
      const data = await response.json();

      if (response.ok && data.parametro) {
        setCargadoHabilitado(data.habilitado);
        setPeriodoInfo(data);
      } else {
        // If no parameter found, enable by default
        setCargadoHabilitado(true);
        setPeriodoInfo(null);
      }
    } catch (error) {
      console.error('Error verificando periodo:', error);
      // On error, enable by default
      setCargadoHabilitado(true);
    }
  };

  const loadRequisitos = async () => {
    try {
      const response = await fetch(`${API_URL}/jefatura/docentes/requisitos?anio=${filters.ano}`);
      const data = await response.json();
      setRequisitos((data.requisitos || []).filter(r => r.estado === 'publicado' || r.estado === 'vencido'));
    } catch (error) {
      console.error('Error loading requisitos:', error);
      setRequisitos([]);
    }
  };

  const loadDocumentos = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/docente/documentos?anio=${filters.ano}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const data = await response.json();
      setDocumentos(data.documentos || []);
    } catch (error) {
      console.error('Error loading documentos:', error);
      setDocumentos([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCarreras = async () => {
    try {
      const response = await fetch(`${API_URL}/docente/carreras`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const data = await response.json();
      setCarreras(data.carreras || []);
    } catch (error) {
      console.error('Error loading carreras:', error);
    }
  };

  const loadMenciones = async (id_carrera) => {
    try {
      const response = await fetch(`${API_URL}/docente/menciones/${id_carrera}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const data = await response.json();
      setMenciones(data.menciones || []);
    } catch (error) {
      console.error('Error loading menciones:', error);
    }
  };

  const loadAsignaturas = async () => {
    try {
      const response = await fetch(`${API_URL}/docente/asignaturas`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const data = await response.json();
      setAsignaturas(data.asignaturas || []);
    } catch (error) {
      console.error('Error loading asignaturas:', error);
    }
  };

  const loadParalelos = async (id_asignatura) => {
    try {
      const response = await fetch(`${API_URL}/docente/paralelos/${id_asignatura}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const data = await response.json();
      setParalelos(data.paralelos || []);
    } catch (error) {
      console.error('Error loading paralelos:', error);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (field === 'id_carrera' && value) {
      loadMenciones(value);
      setFormData(prev => ({ ...prev, id_mencion: '' }));
    }

    if (field === 'id_asignatura' && value) {
      loadParalelos(value);
      setFormData(prev => ({ ...prev, paralelo: '' }));
    }
  };

  const handleFileChange = (e) => {
    setFormData({ ...formData, archivo: e.target.files[0] });
  };

  const resetForm = () => {
    setEditingDoc(null);
    setFormData({
      tipo_documento: '',
      id_requisito: '',
      id_carrera: '',
      id_mencion: '',
      id_asignatura: '',
      paralelo: '',
      archivo: null
    });
    setMenciones([]);
    setParalelos([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar período de cargado
    if (!cargadoHabilitado) {
      alert(`El periodo de cargado de documentación no está habilitado.${periodoInfo?.fecha_inicio ? `\nFecha inicio: ${new Date(periodoInfo.fecha_inicio).toLocaleDateString()}` : ''}${periodoInfo?.fecha_fin ? `\nFecha fin: ${new Date(periodoInfo.fecha_fin).toLocaleDateString()}` : ''}`);
      return;
    }

    try {
      setSaving(true);

      // Usar FormData para enviar archivo
      const submitFormData = new FormData();
      // Formatear fecha en local YYYY-MM-DD HH:mm:ss
      const today = new Date();
      const localDate = today.getFullYear() + '-' +
        String(today.getMonth() + 1).padStart(2, '0') + '-' +
        String(today.getDate()).padStart(2, '0') + ' ' +
        String(today.getHours()).padStart(2, '0') + ':' +
        String(today.getMinutes()).padStart(2, '0') + ':' +
        String(today.getSeconds()).padStart(2, '0');

      console.log('[DEBUG] Full formData state:', formData);
      console.log('[DEBUG] Calculated localDate:', localDate);

      submitFormData.append('tipo_documento', formData.tipo_documento || '');
      submitFormData.append('anio', filters.ano || '');
      submitFormData.append('fecha_presentacion', localDate);

      if (formData.id_requisito) {
        console.log('[DEBUG] Appending id_requisito:', formData.id_requisito);
        submitFormData.append('id_requisito', String(formData.id_requisito));
        // alert(`Enviando con requisito ID: ${formData.id_requisito}`); // Temporary debug
      } else {
        console.log('[DEBUG] id_requisito is EMPTY or NULL');
        // if (!window.confirm("No has seleccionado un Requisito de Jefatura. ¿Deseas continuar enviando el documento sin vincularlo?")) return;
      }
      if (formData.id_carrera) submitFormData.append('id_carrera', formData.id_carrera);
      if (formData.id_mencion) submitFormData.append('id_mencion', formData.id_mencion);
      if (formData.id_asignatura) submitFormData.append('id_asignatura', formData.id_asignatura);
      if (formData.paralelo) submitFormData.append('paralelo', formData.paralelo);
      if (formData.archivo) submitFormData.append('archivo', formData.archivo);

      const url = editingDoc
        ? `${API_URL}/docente/documentos/${editingDoc.id_documento}`
        : `${API_URL}/docente/documentos`;

      const method = editingDoc ? 'PUT' : 'POST';

      let response;
      if (editingDoc) {
        // Para editar, usar JSON
        response = await fetch(url, {
          method,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getToken()}`
          },
          body: JSON.stringify({
            tipo_documento: formData.tipo_documento,
            id_requisito: formData.id_requisito || null,
            id_carrera: formData.id_carrera || null,
            id_mencion: formData.id_mencion || null,
            id_asignatura: formData.id_asignatura || null,
            paralelo: formData.paralelo || null,
            fecha_presentacion: localDate // Send freshly generated date with time
          })
        });

        // Si hay archivo nuevo, subirlo por separado
        if (formData.archivo && response.ok) {
          const uploadFormData = new FormData();
          uploadFormData.append('archivo', formData.archivo);
          await fetch(`${API_URL}/docente/documentos/upload/${editingDoc.id_documento}`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${getToken()}` },
            body: uploadFormData
          });
        }
      } else {
        // Para crear nuevo, usar FormData con archivo
        response = await fetch(url, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${getToken()}` },
          body: submitFormData
        });
      }

      if (response.ok) {
        alert(editingDoc ? 'Documento actualizado exitosamente' : 'Documento guardado exitosamente');
        setShowModal(false);
        resetForm();
        loadDocumentos();
      } else {
        const data = await response.json();
        alert(data.message || 'Error al guardar documento');
      }
    } catch (error) {
      console.error('Error saving documento:', error);
      alert('Error al guardar documento');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (doc) => {
    setEditingDoc(doc);

    // Cargar menciones si hay carrera
    if (doc.id_carrera) {
      await loadMenciones(doc.id_carrera);
    }

    // Cargar paralelos si hay asignatura
    if (doc.id_asignatura) {
      await loadParalelos(doc.id_asignatura);
    }

    setFormData({
      tipo_documento: doc.tipo_documento || '',
      id_requisito: doc.id_requisito || '',
      id_carrera: doc.id_carrera || '',
      id_mencion: doc.id_mencion || '',
      id_asignatura: doc.id_asignatura || '',
      paralelo: doc.paralelo || '',
      archivo: null,
      archivo_existente: doc.archivo_url || null
    });
    setShowModal(true);
  };

  const handleView = (doc) => {
    setViewingDoc(doc);
    setShowViewModal(true);
  };

  const getEstadoBadge = (estado, entregaTardia) => {
    const s = (estado || '').toLowerCase();
    if (entregaTardia && s === 'enviado') {
      return 'bg-red-100 text-red-800'; // Override for late shipment
    }
    switch (s) {
      case 'enviado':
        return 'bg-yellow-100 text-yellow-800';
      case 'enviado con retraso':
        return 'bg-red-100 text-red-800';
      case 'revisado':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">

        {/* Alert Banner when period is closed */}
        {!cargadoHabilitado && (
          <div className="mb-4 p-4 bg-yellow-100 border border-yellow-400 rounded-lg flex items-center gap-3">
            <AlertTriangle className="text-yellow-600" size={24} />
            <div>
              <p className="font-medium text-yellow-800">Período de cargado de documentación cerrado</p>
              <p className="text-sm text-yellow-700">
                {periodoInfo?.fecha_inicio && periodoInfo?.fecha_fin
                  ? `Período activo: ${new Date(periodoInfo.fecha_inicio).toLocaleDateString()} - ${new Date(periodoInfo.fecha_fin).toLocaleDateString()}`
                  : 'Consulte con Dirección para más información'}
              </p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="">
          <div className="mb-6">
            <h1 className="text-xl font-bold text-gray-800">Docente &gt; Entrega Documentación</h1>
          </div>
          <div className="flex flex-wrap items-center gap-4 mb-6">
            {/* Filtro Año */}
            <div className="relative">
              <select
                value={filters.ano}
                onChange={(e) => handleFilterChange('ano', e.target.value)}
                className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
            </div>

            {/* Botón Añadir */}
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              disabled={!cargadoHabilitado}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${cargadoHabilitado
                ? 'bg-blue-900 text-white hover:bg-blue-800'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
            >
              <Plus size={18} />
              Añadir Documento
            </button>
          </div>
        </div>

        {/* Requisitos Pendientes */}
        {requisitos.filter(r => !documentos.some(d => d.id_requisito && Number(d.id_requisito) === Number(r.id_requisito))).length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="text-blue-600" size={20} />
              <h3 className="font-semibold text-blue-800">Documentos Solicitados por Jefatura</h3>
            </div>
            <div className="grid gap-2">
              {requisitos
                .filter(r => !documentos.some(d => d.id_requisito && Number(d.id_requisito) === Number(r.id_requisito)))
                .map(r => (
                  <div key={r.id_requisito} className="bg-white p-3 rounded-lg border border-blue-100 flex justify-between items-center">
                    <div>
                      <p className="font-medium">{r.tipo_documento}</p>
                      {r.descripcion && <p className="text-sm text-gray-600">{r.descripcion}</p>}
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Fecha límite:</p>
                      <p className={`font-medium ${new Date() > new Date(r.fecha_limite) ? 'text-red-600' : 'text-green-600'}`}>
                        {new Date(r.fecha_limite).toLocaleString('es-BO', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Tabla de Documentos */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Cargando...</div>
          ) : documentos.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No hay documentos registrados para el año {filters.ano}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-3 text-center font-semibold border">No</th>
                    <th className="px-3 py-3 text-center font-semibold border">Documento Académico</th>
                    <th className="px-3 py-3 text-center font-semibold border">Carrera</th>
                    <th className="px-3 py-3 text-center font-semibold border">Mención</th>
                    <th className="px-3 py-3 text-center font-semibold border">Asignatura</th>
                    <th className="px-3 py-3 text-center font-semibold border">Paralelo</th>
                    <th className="px-3 py-3 text-center font-semibold border">Fecha Presentación</th>
                    <th className="px-3 py-3 text-center font-semibold border">Acciones</th>
                    <th className="px-3 py-3 text-center font-semibold border">Estado</th>
                    <th className="px-3 py-3 text-center font-semibold border">Puntaje</th>
                    <th className="px-3 py-3 text-center font-semibold border">Obs. Jefatura</th>
                  </tr>
                </thead>
                <tbody>
                  {documentos.map((doc, idx) => (
                    <tr key={doc.id_documento} className="hover:bg-gray-50">
                      <td className="p-3 border">{idx + 1}</td>
                      <td className="p-3 max-w-[150px] truncate border" title={doc.tipo_documento}>
                        {doc.tipo_documento}
                      </td>
                      <td className="p-3 max-w-[120px] truncate border" title={doc.carrera_nombre}>
                        {doc.carrera_nombre || '-'}
                      </td>
                      <td className="p-3 max-w-[100px] truncate border" title={doc.mencion_nombre}>
                        {doc.mencion_nombre || '-'}
                      </td>
                      <td className="p-3 max-w-[100px] truncate border" title={doc.asignatura_nombre}>
                        {doc.asignatura_nombre || '-'}
                      </td>
                      <td className="p-3 border">{doc.paralelo || '-'}</td>
                      <td className={`p-3 ${doc.entrega_tardia ? 'text-red-700 font-bold border' : ''}`}>
                        {doc.fecha_presentacion ? new Date(doc.fecha_presentacion).toLocaleString('es-BO', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
                      </td>
                      <td className="p-3 border">
                        <div className="flex gap-2 justify-center">
                          <button
                            onClick={() => handleView(doc)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                            title="Ver documento"
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            onClick={() => handleEdit(doc)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                            title="Editar"
                          >
                            <Edit size={18} />
                          </button>
                        </div>
                      </td>
                      <td className="p-3 border">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getEstadoBadge(doc.estado, doc.entrega_tardia)}`}>
                          {doc.entrega_tardia && (doc.estado || '').toLowerCase() === 'enviado' ? 'enviado con retraso' : (doc.estado || 'pendiente')}
                        </span>
                      </td>
                      <td className="p-3 border">{doc.puntaje_jefatura || '-'}</td>
                      <td className="p-3 max-w-[150px] truncate border" title={doc.comentarios_jefatura}>
                        {doc.comentarios_jefatura || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal Añadir/Editar Documento */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header Modal */}
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-bold text-blue-900">
                {editingDoc ? 'Editar Documento' : 'Añadir Documento'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            {/* Formulario */}
            <form onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Requisito de Jefatura */}
                {requisitos.length > 0 && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Requisito Solicitado por Jefatura
                    </label>
                    <select
                      value={formData.id_requisito}
                      onChange={(e) => {
                        const reqId = e.target.value;
                        const selectedReq = requisitos.find(r => r.id_requisito === parseInt(reqId));
                        handleFormChange('id_requisito', reqId);
                        if (selectedReq) {
                          handleFormChange('tipo_documento', selectedReq.tipo_documento);
                        }
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">-- Seleccionar requisito (opcional) --</option>
                      {requisitos
                        .filter(r => !documentos.some(d => d.id_requisito && Number(d.id_requisito) === Number(r.id_requisito)) || (editingDoc && Number(editingDoc.id_requisito) === Number(r.id_requisito)))
                        .map(r => (
                          <option key={r.id_requisito} value={r.id_requisito}>
                            {r.tipo_documento} - Límite: {new Date(r.fecha_limite).toLocaleString('es-BO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </option>
                        ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Seleccione si está respondiendo a un documento solicitado por Jefatura
                    </p>
                  </div>
                )}

                {/* Nombre del Documento */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Nombre del Documento *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.tipo_documento}
                    onChange={(e) => handleFormChange('tipo_documento', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ej: Plan de Desarrollo Curricular"
                    readOnly={!!formData.id_requisito}
                  />
                </div>

                {/* Carrera */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Carrera
                  </label>
                  <select
                    value={formData.id_carrera}
                    onChange={(e) => handleFormChange('id_carrera', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Seleccionar carrera</option>
                    {carreras.map(c => (
                      <option key={c.id_carrera} value={c.id_carrera}>{c.nombre}</option>
                    ))}
                  </select>
                </div>

                {/* Mención */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Mención
                  </label>
                  <select
                    value={formData.id_mencion}
                    onChange={(e) => handleFormChange('id_mencion', e.target.value)}
                    disabled={!formData.id_carrera}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  >
                    <option value="">Seleccionar mención</option>
                    {menciones.map(m => (
                      <option key={m.id_mencion} value={m.id_mencion}>{m.nombre}</option>
                    ))}
                  </select>
                </div>

                {/* Asignatura */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Asignatura
                  </label>
                  <select
                    value={formData.id_asignatura}
                    onChange={(e) => handleFormChange('id_asignatura', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Seleccionar asignatura</option>
                    {asignaturas.map(a => (
                      <option key={a.id_asignatura} value={a.id_asignatura}>{a.nombre}</option>
                    ))}
                  </select>
                </div>

                {/* Paralelo */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Paralelo
                  </label>
                  <select
                    value={formData.paralelo}
                    onChange={(e) => handleFormChange('paralelo', e.target.value)}
                    disabled={!formData.id_asignatura}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  >
                    <option value="">Seleccionar paralelo</option>
                    {paralelos.map(p => (
                      <option key={p.id_paralelo} value={p.nombre}>Paralelo {p.nombre}</option>
                    ))}
                  </select>
                </div>

                {/* Adjuntar archivo */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Adjuntar Documento
                  </label>

                  {/* Mostrar archivo existente si está editando */}
                  {editingDoc && formData.archivo_existente && !formData.archivo && (
                    <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
                      <Paperclip size={18} className="text-blue-600" />
                      <span className="text-sm text-blue-700 flex-1">
                        Archivo actual: {formData.archivo_existente.split('/').pop()}
                      </span>
                      <span className="text-xs text-gray-500">(Selecciona otro archivo para reemplazar)</span>
                    </div>
                  )}

                  <div className="relative">
                    <input
                      type="file"
                      onChange={handleFileChange}
                      className="hidden"
                      id="file-upload"
                      accept=".pdf,.doc,.docx"
                    />
                    <label
                      htmlFor="file-upload"
                      className="w-full px-4 py-3 border border-dashed border-gray-300 rounded-lg flex items-center justify-center gap-2 cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <Paperclip size={20} className="text-gray-400" />
                      <span className="text-gray-600">
                        {formData.archivo ? formData.archivo.name : (editingDoc ? 'Seleccionar nuevo archivo para reemplazar' : 'Seleccionar archivo (PDF, DOC, DOCX)')}
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Botones */}
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Guardando...' : (editingDoc ? 'Actualizar' : 'Guardar')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Ver Documento */}
      {showViewModal && viewingDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center px-6 py-4 border-b sticky top-0 bg-white z-10">
              <h2 className="text-xl font-bold text-blue-900">Ver Documento</h2>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6">
              <div>
                <label className="text-sm font-semibold text-gray-600">Documento:</label>
                <p className="text-gray-800">{viewingDoc.tipo_documento}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-600">Carrera:</label>
                  <p className="text-gray-800">{viewingDoc.carrera_nombre || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">Mención:</label>
                  <p className="text-gray-800">{viewingDoc.mencion_nombre || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">Asignatura:</label>
                  <p className="text-gray-800">{viewingDoc.asignatura_nombre || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">Paralelo:</label>
                  <p className="text-gray-800">{viewingDoc.paralelo || '-'}</p>
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-600">Estado:</label>
                <span className={`ml-2 px-3 py-1 rounded-full text-xs font-medium capitalize ${getEstadoBadge(viewingDoc.estado)}`}>
                  {viewingDoc.estado || 'pendiente'}
                </span>
              </div>
              {viewingDoc.archivo_url && (
                <div>
                  <label className="text-sm font-semibold text-gray-600">Archivo adjunto:</label>
                  <p className="text-blue-600 underline cursor-pointer">{viewingDoc.archivo_url.split('/').pop()}</p>
                </div>
              )}
              {viewingDoc.comentarios_jefatura && (
                <div>
                  <label className="text-sm font-semibold text-gray-600">Observaciones Jefatura:</label>
                  <p className="text-gray-800">{viewingDoc.comentarios_jefatura}</p>
                </div>
              )}
              {viewingDoc.puntaje_jefatura && (
                <div>
                  <label className="text-sm font-semibold text-gray-600">Puntaje:</label>
                  <p className="text-gray-800 font-bold">{viewingDoc.puntaje_jefatura}</p>
                </div>
              )}
            </div>

            <div className="p-6 border-t flex justify-end">
              <button
                onClick={() => setShowViewModal(false)}
                className="px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
